import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Table, Button, Badge, Modal, Form, Spinner, Tab, Nav, ProgressBar } from 'react-bootstrap';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiPlus,
  FiUpload,
  FiRefreshCw,
  FiTrash2,
  FiEdit,
  FiDownload,
  FiPieChart,
  FiBarChart2,
  FiCheckCircle,
  FiFilter,
  FiCalendar,
  FiFileText,
  FiSearch
} from 'react-icons/fi';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import Swal from 'sweetalert2';
import {
  fetchFinanceStatsApi,
  fetchIncomesApi,
  createIncomeApi,
  updateIncomeApi,
  deleteIncomeApi,
  bulkUploadIncomesApi,
  fetchExpensesApi,
  createExpenseApi,
  updateExpenseApi,
  deleteExpenseApi,
  bulkUploadExpensesApi,
  syncAuditionIncomeApi,
  fetchPNLReportApi
} from '../../services/adminApi';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#64748b'];

const AdminFinancePage = () => {
  const [activeTab, setActiveTab] = useState('pnl');

  // Stats & Filters
  const [stats, setStats] = useState({
    summary: { totalIncome: 0, totalExpense: 0, netProfit: 0, profitMargin: 0, incomeCount: 0, expenseCount: 0 },
    incomeByCategory: [],
    expenseByCategory: [],
    incomeBySource: [],
    recentIncomes: [],
    recentExpenses: []
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });

  // Incomes State
  const [incomes, setIncomes] = useState([]);
  const [incomeLoading, setIncomeLoading] = useState(false);
  const [incomeSearch, setIncomeSearch] = useState('');
  const [incomeCategory, setIncomeCategory] = useState('');
  const [incomePage, setIncomePage] = useState(1);
  const [incomeTotalPages, setIncomeTotalPages] = useState(1);

  // Expenses State
  const [expenses, setExpenses] = useState([]);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [expenseSearch, setExpenseSearch] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expensePage, setExpensePage] = useState(1);
  const [expenseTotalPages, setExpenseTotalPages] = useState(1);

  // P&L Statement State
  const [pnlReport, setPnlReport] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);

  // Modals
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [incomeForm, setIncomeForm] = useState({
    title: '', source: 'MANUAL', category: 'Audition Fee', amount: '', income_date: new Date().toISOString().slice(0, 10), payment_method: 'UPI', reference_id: '', notes: '', status: 'RECEIVED'
  });

  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    title: '', category: 'Misc', amount: '', expense_date: new Date().toISOString().slice(0, 10), payment_method: 'UPI', vendor_name: '', reference_id: '', notes: '', status: 'PAID'
  });

  // Bulk Upload Modals
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkType, setBulkType] = useState('income'); // 'income' or 'expense'
  const [bulkInput, setBulkInput] = useState('');

  // 1. Fetch Stats & PNL Overview
  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await fetchFinanceStatsApi(dateFilter);
      if (res.data?.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error("Stats Error:", err);
    } finally {
      setLoadingStats(false);
    }
  }, [dateFilter]);

  // 2. Fetch Incomes List
  const loadIncomes = useCallback(async () => {
    setIncomeLoading(true);
    try {
      const res = await fetchIncomesApi({
        page: incomePage,
        limit: 15,
        search: incomeSearch,
        category: incomeCategory,
        ...dateFilter
      });
      if (res.data?.success) {
        setIncomes(res.data.data.incomes || []);
        setIncomeTotalPages(res.data.data.totalPages || 1);
      }
    } catch (err) {
      console.error("Fetch Incomes Error:", err);
    } finally {
      setIncomeLoading(false);
    }
  }, [incomePage, incomeSearch, incomeCategory, dateFilter]);

  // 3. Fetch Expenses List
  const loadExpenses = useCallback(async () => {
    setExpenseLoading(true);
    try {
      const res = await fetchExpensesApi({
        page: expensePage,
        limit: 15,
        search: expenseSearch,
        category: expenseCategory,
        ...dateFilter
      });
      if (res.data?.success) {
        setExpenses(res.data.data.expenses || []);
        setExpenseTotalPages(res.data.data.totalPages || 1);
      }
    } catch (err) {
      console.error("Fetch Expenses Error:", err);
    } finally {
      setExpenseLoading(false);
    }
  }, [expensePage, expenseSearch, expenseCategory, dateFilter]);

  // 4. Fetch PNL Statement Report
  const loadPNLReport = useCallback(async () => {
    setPnlLoading(true);
    try {
      const res = await fetchPNLReportApi(dateFilter);
      if (res.data?.success) {
        setPnlReport(res.data.data);
      }
    } catch (err) {
      console.error("PNL Report Error:", err);
    } finally {
      setPnlLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (activeTab === 'income') loadIncomes();
    if (activeTab === 'expense') loadExpenses();
    if (activeTab === 'report') loadPNLReport();
  }, [activeTab, loadIncomes, loadExpenses, loadPNLReport]);

  // Auto-Sync Audition Incomes
  const handleSyncAuditionIncomes = async () => {
    try {
      Swal.fire({ title: 'Syncing Audition Payments...', text: 'Scanning booked auditions...', didOpen: () => Swal.showLoading() });
      const res = await syncAuditionIncomeApi();
      if (res.data?.success) {
        Swal.fire({ icon: 'success', title: 'Sync Completed!', text: res.data.message || 'Incomes updated.' });
        loadStats();
        if (activeTab === 'income') loadIncomes();
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Sync Failed', text: err.response?.data?.message || 'Could not sync audition payments' });
    }
  };

  // Save Income Form (Create / Edit)
  const handleSaveIncome = async (e) => {
    e.preventDefault();
    try {
      if (editingIncome) {
        await updateIncomeApi(editingIncome.id, incomeForm);
        Swal.fire({ icon: 'success', title: 'Updated!', text: 'Income record updated successfully.' });
      } else {
        await createIncomeApi(incomeForm);
        Swal.fire({ icon: 'success', title: 'Saved!', text: 'New income record added.' });
      }
      setShowIncomeModal(false);
      setEditingIncome(null);
      loadStats();
      loadIncomes();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to save income' });
    }
  };

  // Save Expense Form (Create / Edit)
  const handleSaveExpense = async (e) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await updateExpenseApi(editingExpense.id, expenseForm);
        Swal.fire({ icon: 'success', title: 'Updated!', text: 'Expense record updated successfully.' });
      } else {
        await createExpenseApi(expenseForm);
        Swal.fire({ icon: 'success', title: 'Saved!', text: 'New expense record added.' });
      }
      setShowExpenseModal(false);
      setEditingExpense(null);
      loadStats();
      loadExpenses();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed to save expense' });
    }
  };

  // Delete Handlers
  const handleDeleteIncome = (id) => {
    Swal.fire({
      title: 'Delete Income Record?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, Delete',
      background: '#111827',
      color: '#fff'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteIncomeApi(id);
          Swal.fire({ icon: 'success', title: 'Deleted!' });
          loadStats();
          loadIncomes();
        } catch (err) {
          Swal.fire({ icon: 'error', title: 'Delete Failed' });
        }
      }
    });
  };

  const handleDeleteExpense = (id) => {
    Swal.fire({
      title: 'Delete Expense Record?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Yes, Delete',
      background: '#111827',
      color: '#fff'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteExpenseApi(id);
          Swal.fire({ icon: 'success', title: 'Deleted!' });
          loadStats();
          loadExpenses();
        } catch (err) {
          Swal.fire({ icon: 'error', title: 'Delete Failed' });
        }
      }
    });
  };

  // Bulk Upload Processing (JSON / CSV Text)
  const handleBulkSubmit = async () => {
    if (!bulkInput.trim()) {
      return Swal.fire({ icon: 'warning', title: 'Input Required', text: 'Please paste CSV or JSON data.' });
    }

    let parsedItems = [];
    try {
      // Try JSON parse first
      parsedItems = JSON.parse(bulkInput);
    } catch (e) {
      // Parse CSV line by line
      const lines = bulkInput.trim().split('\n');
      if (lines.length < 2) {
        return Swal.fire({ icon: 'error', title: 'CSV Error', text: 'CSV must have a header line and data rows.' });
      }
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      parsedItems = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i] || '';
        });
        return obj;
      });
    }

    try {
      Swal.fire({ title: 'Processing Bulk Upload...', didOpen: () => Swal.showLoading() });
      if (bulkType === 'income') {
        const res = await bulkUploadIncomesApi({ items: parsedItems });
        Swal.fire({ icon: 'success', title: 'Success!', text: res.data?.message || 'Bulk incomes uploaded.' });
        loadIncomes();
      } else {
        const res = await bulkUploadExpensesApi({ items: parsedItems });
        Swal.fire({ icon: 'success', title: 'Success!', text: res.data?.message || 'Bulk expenses uploaded.' });
        loadExpenses();
      }
      loadStats();
      setShowBulkModal(false);
      setBulkInput('');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Bulk Upload Error', text: err.response?.data?.message || 'Failed to process bulk payload' });
    }
  };

  // Export P&L Report CSV
  const handleExportCSV = () => {
    if (!pnlReport) return;
    let csv = `Profit & Loss Statement (${pnlReport.startDate} to ${pnlReport.endDate})\n\n`;
    csv += `Total Income,₹${pnlReport.totalIncome}\n`;
    csv += `Total Expenses,₹${pnlReport.totalExpense}\n`;
    csv += `Net Profit/Loss,₹${pnlReport.netProfit}\n`;
    csv += `Profit Margin,${pnlReport.profitMargin}%\n\n`;

    csv += `INCOME BREAKDOWN\n`;
    csv += `Code,Title,Source,Category,Amount,Date,Payment Method,Status\n`;
    pnlReport.incomes.forEach(i => {
      csv += `"${i.income_code}","${i.title}","${i.source}","${i.category}",${i.amount},"${i.income_date}","${i.payment_method}","${i.status}"\n`;
    });

    csv += `\nEXPENSE BREAKDOWN\n`;
    csv += `Code,Title,Category,Vendor,Amount,Date,Payment Method,Status\n`;
    pnlReport.expenses.forEach(e => {
      csv += `"${e.expense_code}","${e.title}","${e.category}","${e.vendor_name || ''}",${e.amount},"${e.expense_date}","${e.payment_method}","${e.status}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PNL_Report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const summary = stats.summary || {};
  const isProfitable = (summary.netProfit || 0) >= 0;

  return (
    <div className="container-fluid p-3 p-md-4 text-white" style={{  minHeight: '100vh' }}>
      {/* Header Banner */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom border-secondary border-opacity-25 gap-3">
        <div>
          <div className="d-flex align-items-center gap-2">
            <div className="p-2 rounded-3 text-white" style={{ background: '#dc2626' }}>
              <FiDollarSign size={22} />
            </div>
            <div>
              <h4 className="fw-bold mb-0 text-white">Finance & P&L Management</h4>
              <small className="text-secondary">Track incomes, expenses, bulk uploads & profit/loss statements</small>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            variant="outline-danger"
            className="btn-sm fw-semibold d-inline-flex align-items-center gap-1"
            onClick={handleSyncAuditionIncomes}
          >
            <FiRefreshCw /> Auto-Sync Audition Incomes
          </Button>

          <Button
            variant="danger"
            className="btn-sm fw-semibold d-inline-flex align-items-center gap-1"
            onClick={() => {
              setEditingIncome(null);
              setIncomeForm({ title: '', source: 'MANUAL', category: 'Audition Fee', amount: '', income_date: new Date().toISOString().slice(0, 10), payment_method: 'UPI', reference_id: '', notes: '', status: 'RECEIVED' });
              setShowIncomeModal(true);
            }}
          >
            <FiPlus /> Add Income
          </Button>

          <Button
            variant="outline-warning"
            className="btn-sm fw-semibold d-inline-flex align-items-center gap-1"
            onClick={() => {
              setEditingExpense(null);
              setExpenseForm({ title: '', category: 'Misc', amount: '', expense_date: new Date().toISOString().slice(0, 10), payment_method: 'UPI', vendor_name: '', reference_id: '', notes: '', status: 'PAID' });
              setShowExpenseModal(true);
            }}
          >
            <FiPlus /> Add Expense
          </Button>
        </div>
      </div>

      {/* Overview Stat Cards */}
      <Row className="g-3 mb-4">
        <Col lg={3} sm={6}>
          <Card className="border-0 rounded-4 shadow-sm bg-dark text-white p-3 border border-secondary border-opacity-25 h-100">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <span className="text-secondary small fw-bold text-uppercase">Total Income</span>
                <h3 className="m-0 mt-1 fw-bold text-success">₹{(summary.totalIncome || 0).toLocaleString('en-IN')}</h3>
              </div>
              <div className="p-2 rounded-3 text-success" style={{ background: 'rgba(16, 185, 129, 0.15)' }}>
                <FiTrendingUp size={20} />
              </div>
            </div>
            <small className="text-secondary">{summary.incomeCount || 0} Total Income Records</small>
          </Card>
        </Col>

        <Col lg={3} sm={6}>
          <Card className="border-0 rounded-4 shadow-sm bg-dark text-white p-3 border border-secondary border-opacity-25 h-100">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <span className="text-secondary small fw-bold text-uppercase">Total Expenses</span>
                <h3 className="m-0 mt-1 fw-bold text-danger">₹{(summary.totalExpense || 0).toLocaleString('en-IN')}</h3>
              </div>
              <div className="p-2 rounded-3 text-danger" style={{ background: 'rgba(220, 38, 38, 0.15)' }}>
                <FiTrendingDown size={20} />
              </div>
            </div>
            <small className="text-secondary">{summary.expenseCount || 0} Total Expense Records</small>
          </Card>
        </Col>

        <Col lg={3} sm={6}>
          <Card className="border-0 rounded-4 shadow-sm bg-dark text-white p-3 border border-secondary border-opacity-25 h-100">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <span className="text-secondary small fw-bold text-uppercase">Net Profit / Loss</span>
                <h3 className={`m-0 mt-1 fw-bold ${isProfitable ? 'text-success' : 'text-danger'}`}>
                  ₹{(summary.netProfit || 0).toLocaleString('en-IN')}
                </h3>
              </div>
              <Badge bg={isProfitable ? 'success' : 'danger'} className="p-2">
                {isProfitable ? 'PROFITABLE' : 'LOSS'}
              </Badge>
            </div>
            <small className="text-secondary">Overall P&L Balance</small>
          </Card>
        </Col>

        <Col lg={3} sm={6}>
          <Card className="border-0 rounded-4 shadow-sm bg-dark text-white p-3 border border-secondary border-opacity-25 h-100">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <span className="text-secondary small fw-bold text-uppercase">Net Profit Margin</span>
                <h3 className="m-0 mt-1 fw-bold text-warning">{summary.profitMargin || 0}%</h3>
              </div>
              <div className="p-2 rounded-3 text-warning" style={{ background: 'rgba(245, 158, 11, 0.15)' }}>
                <FiPieChart size={20} />
              </div>
            </div>
            <small className="text-secondary">Return on Total Revenue</small>
          </Card>
        </Col>
      </Row>

      {/* Main Tabs Navigation */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav variant="pills" className="bg-dark p-1.5 rounded-3 mb-4 gap-2 border border-secondary border-opacity-25">
          <Nav.Item>
            <Nav.Link eventKey="pnl" className="text-white fw-bold px-3 py-2 rounded-3">
              📊 P&L Overview & Analytics
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="income" className="text-white fw-bold px-3 py-2 rounded-3">
              💰 Income Manager
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="expense" className="text-white fw-bold px-3 py-2 rounded-3">
              💸 Expense Manager
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="report" className="text-white fw-bold px-3 py-2 rounded-3">
              📄 P&L Statement Report
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* TAB 1: P&L OVERVIEW & ANALYTICS */}
          <Tab.Pane eventKey="pnl">
            {loadingStats ? (
              <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>
            ) : (
              <div className="d-flex flex-column gap-4">
                {/* 1. TOP SECTION: FINANCIAL COMPARISON BAR CHART & SUMMARY METRICS */}
                <Row className="g-4">
                  <Col lg={7}>
                    <Card className="border-0 shadow-sm rounded-4 bg-dark text-white p-3 border border-secondary border-opacity-25 h-100">
                      <Card.Header className="bg-transparent border-0 px-0 pt-0 pb-3 d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="m-0 fw-bold text-white d-flex align-items-center gap-2">
                            <FiBarChart2 className="text-danger" /> Revenue vs Expense Comparison
                          </h6>
                          <small className="text-secondary">Visual comparison of Total Revenue, Total Expenses, and Net Profit</small>
                        </div>
                        <Badge bg="outline-light" className="text-warning border border-warning px-2 py-1">Overall P&L</Badge>
                      </Card.Header>
                      <div style={{ width: '100%', height: 240 }}>
                        <ResponsiveContainer>
                          <BarChart
                            data={[
                              { name: 'Total Revenue', amount: summary.totalIncome || 0, fill: '#10b981' },
                              { name: 'Total Expense', amount: summary.totalExpense || 0, fill: '#ef4444' },
                              { name: 'Net Profit', amount: Math.max(0, summary.netProfit || 0), fill: '#3b82f6' }
                            ]}
                            margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.5} />
                            <XAxis dataKey="name" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" tickFormatter={(v) => `₹${v}`} />
                            <Tooltip formatter={(val) => [`₹${Number(val).toLocaleString('en-IN')}`, 'Amount']} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', color: '#fff' }} />
                            <Bar dataKey="amount" radius={[8, 8, 0, 0]} barSize={45}>
                              {
                                [
                                  { fill: '#10b981' },
                                  { fill: '#ef4444' },
                                  { fill: '#3b82f6' }
                                ].map((entry, index) => (
                                  <Cell key={`bar-${index}`} fill={entry.fill} />
                                ))
                              }
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  </Col>

                  <Col lg={5}>
                    <Card className="border-0 shadow-sm rounded-4 bg-dark text-white p-3 border border-secondary border-opacity-25 h-100 d-flex flex-column justify-content-between">
                      <div>
                        <Card.Header className="bg-transparent border-0 px-0 pt-0 pb-2 mb-2 border-bottom border-secondary border-opacity-25">
                          <h6 className="m-0 fw-bold text-white d-flex align-items-center gap-2">
                            <FiCheckCircle className="text-success" /> Financial Performance Metrics
                          </h6>
                        </Card.Header>

                        <div className="bg-black p-3 rounded-3 mb-3 border border-secondary border-opacity-25">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-secondary small">Audition Revenue Auto-Sync</span>
                            <Badge bg="primary">ACTIVE</Badge>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold text-white">{summary.incomeCount || 0} Audition Incomes</span>
                            <span className="text-success fw-bold">₹{(summary.totalIncome || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>

                        <div className="bg-black p-3 rounded-3 mb-3 border border-secondary border-opacity-25">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-secondary small">Recorded Expense Operations</span>
                            <Badge bg="danger">TRACKED</Badge>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold text-white">{summary.expenseCount || 0} Expense Records</span>
                            <span className="text-danger fw-bold">₹{(summary.totalExpense || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>

                        <div className="bg-black p-3 rounded-3 border border-secondary border-opacity-25">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-secondary small">Net Profit Margin Rate</span>
                            <span className="text-warning fw-bold fs-5">{summary.profitMargin || 0}%</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* 2. SECOND SECTION: CATEGORY BREAKDOWN CARDS WITH DONUT CHARTS */}
                <Row className="g-4">
                  {/* Income Category Breakdown */}
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm rounded-4 bg-dark text-white p-3 border border-secondary border-opacity-25 h-100">
                      <Card.Header className="bg-transparent border-0 px-0 pt-0 pb-2 d-flex justify-content-between align-items-center border-bottom border-secondary border-opacity-25 mb-3">
                        <h6 className="m-0 fw-bold text-white">Income Breakdown by Category</h6>
                        <Badge bg="success" className="px-2 py-1 fs-6">₹{(summary.totalIncome || 0).toLocaleString('en-IN')}</Badge>
                      </Card.Header>

                      {stats.incomeByCategory && stats.incomeByCategory.length > 0 ? (
                        <Row className="align-items-center g-3">
                          <Col sm={5}>
                            <div style={{ width: '100%', height: 180 }} className="d-flex align-items-center justify-content-center">
                              <ResponsiveContainer>
                                <PieChart>
                                  <Pie
                                    data={stats.incomeByCategory}
                                    dataKey="totalAmount"
                                    nameKey="category"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={48}
                                    outerRadius={72}
                                    paddingAngle={3}
                                  >
                                    {stats.incomeByCategory.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(val) => `₹${Number(val).toLocaleString('en-IN')}`} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151' }} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          </Col>

                          <Col sm={7}>
                            <div className="d-flex flex-column gap-2">
                              {stats.incomeByCategory.map((cat, idx) => {
                                const amt = Number(cat.totalAmount || 0);
                                const totalInc = Number(summary.totalIncome || 0);
                                const pct = totalInc > 0 ? ((amt / totalInc) * 100).toFixed(1) : 0;
                                return (
                                  <div key={idx} className="bg-black p-2.5 rounded-3 border border-secondary border-opacity-25">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span className="fw-semibold text-white small d-flex align-items-center gap-1.5">
                                        <span
                                          className="d-inline-block rounded-circle"
                                          style={{ width: '8px', height: '8px', backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                                        ></span>
                                        {cat.category}
                                      </span>
                                      <Badge bg="dark" className="border border-secondary text-secondary small">{cat.count} records</Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center small">
                                      <span className="text-secondary small">{pct}% of total</span>
                                      <span className="text-success fw-bold">₹{amt.toLocaleString('en-IN')}</span>
                                    </div>
                                    <ProgressBar now={parseFloat(pct)} variant="success" style={{ height: '4px', marginTop: '6px' }} />
                                  </div>
                                );
                              })}
                            </div>
                          </Col>
                        </Row>
                      ) : (
                        <div className="text-center text-secondary py-4">
                          <FiPieChart size={32} className="mb-2 opacity-50" />
                          <p className="m-0 small">No income entries found.</p>
                        </div>
                      )}
                    </Card>
                  </Col>

                  {/* Expense Category Breakdown */}
                  <Col lg={6}>
                    <Card className="border-0 shadow-sm rounded-4 bg-dark text-white p-3 border border-secondary border-opacity-25 h-100">
                      <Card.Header className="bg-transparent border-0 px-0 pt-0 pb-2 d-flex justify-content-between align-items-center border-bottom border-secondary border-opacity-25 mb-3">
                        <h6 className="m-0 fw-bold text-white">Expense Breakdown by Category</h6>
                        <Badge bg="danger" className="px-2 py-1 fs-6">₹{(summary.totalExpense || 0).toLocaleString('en-IN')}</Badge>
                      </Card.Header>

                      {stats.expenseByCategory && stats.expenseByCategory.length > 0 ? (
                        <Row className="align-items-center g-3">
                          <Col sm={5}>
                            <div style={{ width: '100%', height: 180 }} className="d-flex align-items-center justify-content-center">
                              <ResponsiveContainer>
                                <PieChart>
                                  <Pie
                                    data={stats.expenseByCategory}
                                    dataKey="totalAmount"
                                    nameKey="category"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={48}
                                    outerRadius={72}
                                    paddingAngle={3}
                                  >
                                    {stats.expenseByCategory.map((entry, index) => (
                                      <Cell key={`cell-exp-${index}`} fill={CHART_COLORS[(index + 3) % CHART_COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(val) => `₹${Number(val).toLocaleString('en-IN')}`} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151' }} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          </Col>

                          <Col sm={7}>
                            <div className="d-flex flex-column gap-2">
                              {stats.expenseByCategory.map((cat, idx) => {
                                const amt = Number(cat.totalAmount || 0);
                                const totalExp = Number(summary.totalExpense || 0);
                                const pct = totalExp > 0 ? ((amt / totalExp) * 100).toFixed(1) : 0;
                                return (
                                  <div key={idx} className="bg-black p-2.5 rounded-3 border border-secondary border-opacity-25">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span className="fw-semibold text-white small d-flex align-items-center gap-1.5">
                                        <span
                                          className="d-inline-block rounded-circle"
                                          style={{ width: '8px', height: '8px', backgroundColor: CHART_COLORS[(idx + 3) % CHART_COLORS.length] }}
                                        ></span>
                                        {cat.category}
                                      </span>
                                      <Badge bg="dark" className="border border-secondary text-secondary small">{cat.count} records</Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center small">
                                      <span className="text-secondary small">{pct}% of total</span>
                                      <span className="text-danger fw-bold">₹{amt.toLocaleString('en-IN')}</span>
                                    </div>
                                    <ProgressBar now={parseFloat(pct)} variant="danger" style={{ height: '4px', marginTop: '6px' }} />
                                  </div>
                                );
                              })}
                            </div>
                          </Col>
                        </Row>
                      ) : (
                        <div className="text-center text-secondary py-4">
                          <FiPieChart size={32} className="mb-2 opacity-50" />
                          <p className="m-0 small">No expense entries found.</p>
                        </div>
                      )}
                    </Card>
                  </Col>
                </Row>
              </div>
            )}
          </Tab.Pane>

          {/* TAB 2: INCOME MANAGER */}
          <Tab.Pane eventKey="income">
            <Card className="border-0 shadow-sm rounded-4 bg-dark text-white p-3 border border-secondary border-opacity-25">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Search Title / Ref..."
                    className="bg-black text-white border-secondary btn-sm"
                    style={{ width: '220px' }}
                    value={incomeSearch}
                    onChange={(e) => setIncomeSearch(e.target.value)}
                  />
                  <Form.Select
                    className="bg-black text-white border-secondary btn-sm"
                    style={{ width: '180px' }}
                    value={incomeCategory}
                    onChange={(e) => setIncomeCategory(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    <option value="Audition Fee">Audition Fee</option>
                    <option value="Platform Commission">Platform Commission</option>
                    <option value="Direct Client">Direct Client</option>
                    <option value="Sponsorship">Sponsorship</option>
                    <option value="Misc">Misc</option>
                  </Form.Select>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-light"
                    className="btn-sm fw-semibold"
                    onClick={() => {
                      setBulkType('income');
                      setShowBulkModal(true);
                    }}
                  >
                    <FiUpload /> Bulk CSV Upload
                  </Button>
                  <Button
                    variant="danger"
                    className="btn-sm fw-semibold"
                    onClick={() => {
                      setEditingIncome(null);
                      setIncomeForm({ title: '', source: 'MANUAL', category: 'Audition Fee', amount: '', income_date: new Date().toISOString().slice(0, 10), payment_method: 'UPI', reference_id: '', notes: '', status: 'RECEIVED' });
                      setShowIncomeModal(true);
                    }}
                  >
                    <FiPlus /> Add Income
                  </Button>
                </div>
              </div>

              {incomeLoading ? (
                <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>
              ) : (
                <div className="table-responsive">
                  <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless align-middle">
                    <thead className="border-bottom border-secondary border-opacity-25">
                      <tr>
                        <th>Code</th>
                        <th>Title</th>
                        <th>Source</th>
                        <th>Category</th>
                        <th>Amount (₹)</th>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {incomes.length > 0 ? (
                        incomes.map((item) => (
                          <tr key={item.id}>
                            <td className="fw-mono text-danger fw-bold">{item.income_code || `INC-${item.id}`}</td>
                            <td className="fw-semibold">{item.title}</td>
                            <td>
                              <Badge bg={item.source === 'AUDITION_APPLICATION' ? 'primary' : 'secondary'}>
                                {item.source}
                              </Badge>
                            </td>
                            <td><small className="text-secondary">{item.category}</small></td>
                            <td className="fw-bold text-success">₹{parseFloat(item.amount || 0).toLocaleString('en-IN')}</td>
                            <td><small className="text-secondary">{item.income_date}</small></td>
                            <td><small>{item.payment_method}</small></td>
                            <td>
                              <Badge bg={item.status === 'RECEIVED' ? 'success' : 'warning'}>{item.status}</Badge>
                            </td>
                            <td className="text-center">
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                <button
                                  type="button"
                                  title="Edit Income"
                                  style={{
                                    background: '#2563eb',
                                    backgroundColor: '#2563eb',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                    flexShrink: 0
                                  }}
                                  onClick={() => {
                                    setEditingIncome(item);
                                    setIncomeForm({ ...item });
                                    setShowIncomeModal(true);
                                  }}
                                >
                                  <FiEdit size={15} color="#ffffff" />
                                </button>
                                <button
                                  type="button"
                                  title="Delete Income"
                                  style={{
                                    background: '#dc2626',
                                    backgroundColor: '#dc2626',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                    flexShrink: 0
                                  }}
                                  onClick={() => handleDeleteIncome(item.id)}
                                >
                                  <FiTrash2 size={15} color="#ffffff" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="9" className="text-center text-secondary py-4">No income entries found.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card>
          </Tab.Pane>

          {/* TAB 3: EXPENSE MANAGER */}
          <Tab.Pane eventKey="expense">
            <Card className="border-0 shadow-sm rounded-4 bg-dark text-white p-3 border border-secondary border-opacity-25">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Search Title / Vendor..."
                    className="bg-black text-white border-secondary btn-sm"
                    style={{ width: '220px' }}
                    value={expenseSearch}
                    onChange={(e) => setExpenseSearch(e.target.value)}
                  />
                  <Form.Select
                    className="bg-black text-white border-secondary btn-sm"
                    style={{ width: '180px' }}
                    value={expenseCategory}
                    onChange={(e) => setExpenseCategory(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    <option value="Hosting & Server">Hosting & Server</option>
                    <option value="Marketing & Ads">Marketing & Ads</option>
                    <option value="Salary / Payroll">Salary / Payroll</option>
                    <option value="Software License">Software License</option>
                    <option value="Office Maintenance">Office Maintenance</option>
                    <option value="Gateway Charges">Gateway Charges</option>
                    <option value="Misc">Misc</option>
                  </Form.Select>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-light"
                    className="btn-sm fw-semibold"
                    onClick={() => {
                      setBulkType('expense');
                      setShowBulkModal(true);
                    }}
                  >
                    <FiUpload /> Bulk CSV Upload
                  </Button>
                  <Button
                    variant="warning"
                    className="btn-sm fw-semibold"
                    onClick={() => {
                      setEditingExpense(null);
                      setExpenseForm({ title: '', category: 'Misc', amount: '', expense_date: new Date().toISOString().slice(0, 10), payment_method: 'UPI', vendor_name: '', reference_id: '', notes: '', status: 'PAID' });
                      setShowExpenseModal(true);
                    }}
                  >
                    <FiPlus /> Add Expense
                  </Button>
                </div>
              </div>

              {expenseLoading ? (
                <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>
              ) : (
                <div className="table-responsive">
                  <Table hover align="middle" className="m-0 text-nowrap table-dark table-borderless align-middle">
                    <thead className="border-bottom border-secondary border-opacity-25">
                      <tr>
                        <th>Code</th>
                        <th>Title</th>
                        <th>Category</th>
                        <th>Vendor</th>
                        <th>Amount (₹)</th>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.length > 0 ? (
                        expenses.map((item) => (
                          <tr key={item.id}>
                            <td className="fw-mono text-warning fw-bold">{item.expense_code || `EXP-${item.id}`}</td>
                            <td className="fw-semibold">{item.title}</td>
                            <td><small className="text-secondary">{item.category}</small></td>
                            <td><small className="text-white">{item.vendor_name || 'N/A'}</small></td>
                            <td className="fw-bold text-danger">₹{parseFloat(item.amount || 0).toLocaleString('en-IN')}</td>
                            <td><small className="text-secondary">{item.expense_date}</small></td>
                            <td><small>{item.payment_method}</small></td>
                            <td>
                              <Badge bg={item.status === 'PAID' ? 'danger' : 'warning'}>{item.status}</Badge>
                            </td>
                            <td className="text-center">
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                <button
                                  type="button"
                                  title="Edit Expense"
                                  style={{
                                    background: '#2563eb',
                                    backgroundColor: '#2563eb',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                    flexShrink: 0
                                  }}
                                  onClick={() => {
                                    setEditingExpense(item);
                                    setExpenseForm({ ...item });
                                    setShowExpenseModal(true);
                                  }}
                                >
                                  <FiEdit size={15} color="#ffffff" />
                                </button>
                                <button
                                  type="button"
                                  title="Delete Expense"
                                  style={{
                                    background: '#dc2626',
                                    backgroundColor: '#dc2626',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0,
                                    flexShrink: 0
                                  }}
                                  onClick={() => handleDeleteExpense(item.id)}
                                >
                                  <FiTrash2 size={15} color="#ffffff" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan="9" className="text-center text-secondary py-4">No expense entries found.</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card>
          </Tab.Pane>

          {/* TAB 4: P&L STATEMENT REPORT */}
          <Tab.Pane eventKey="report">
            <Card className="border-0 shadow-sm rounded-4 bg-dark text-white p-3 border border-secondary border-opacity-25">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="fw-bold mb-0 text-white">Profit & Loss (P&L) Statement</h5>
                  <small className="text-secondary">Complete revenue vs expense audit report</small>
                </div>
                <Button variant="danger" className="btn-sm fw-semibold" onClick={handleExportCSV}>
                  <FiDownload /> Export P&L Report (CSV)
                </Button>
              </div>

              {pnlLoading ? (
                <div className="text-center p-5"><Spinner animation="border" variant="danger" /></div>
              ) : pnlReport ? (
                <div className="p-3 bg-black rounded-3 border border-secondary border-opacity-25">
                  <h6 className="fw-bold text-success border-bottom border-secondary border-opacity-25 pb-2 mb-3">1. Revenue & Income Items ({pnlReport.incomes?.length || 0})</h6>
                  <Table hover size="sm" className="table-dark table-borderless text-nowrap mb-4">
                    <thead>
                      <tr className="text-secondary">
                        <th>Date</th><th>Code</th><th>Title</th><th>Category</th><th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pnlReport.incomes?.map(i => (
                        <tr key={i.id}>
                          <td>{i.income_date}</td><td>{i.income_code}</td><td>{i.title}</td><td>{i.category}</td>
                          <td className="text-end text-success fw-bold">₹{parseFloat(i.amount).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <h6 className="fw-bold text-danger border-bottom border-secondary border-opacity-25 pb-2 mb-3">2. Expense Items ({pnlReport.expenses?.length || 0})</h6>
                  <Table hover size="sm" className="table-dark table-borderless text-nowrap">
                    <thead>
                      <tr className="text-secondary">
                        <th>Date</th><th>Code</th><th>Title</th><th>Category</th><th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pnlReport.expenses?.map(e => (
                        <tr key={e.id}>
                          <td>{e.expense_date}</td><td>{e.expense_code}</td><td>{e.title}</td><td>{e.category}</td>
                          <td className="text-end text-danger fw-bold">₹{parseFloat(e.amount).toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : null}
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* MODAL: ADD / EDIT INCOME */}
      <Modal show={showIncomeModal} onHide={() => setShowIncomeModal(false)} centered background="#111827">
        <Modal.Header closeButton className="bg-dark text-white border-secondary">
          <Modal.Title className="fs-6 fw-bold">{editingIncome ? 'Edit Income Entry' : 'Add Manual Income Entry'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveIncome}>
          <Modal.Body className="bg-dark text-white">
            <Form.Group className="mb-3">
              <Form.Label className="small text-secondary">Income Title / Description *</Form.Label>
              <Form.Control
                type="text"
                required
                placeholder="e.g. Sponsorship Fee / Audition Payment"
                className="bg-black text-white border-secondary"
                value={incomeForm.title}
                onChange={(e) => setIncomeForm({ ...incomeForm, title: e.target.value })}
              />
            </Form.Group>

            <Row className="g-3 mb-3">
              <Col col={6}>
                <Form.Label className="small text-secondary">Amount (INR) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  required
                  placeholder="0.00"
                  className="bg-black text-white border-secondary"
                  value={incomeForm.amount}
                  onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                />
              </Col>
              <Col col={6}>
                <Form.Label className="small text-secondary">Category</Form.Label>
                <Form.Select
                  className="bg-black text-white border-secondary"
                  value={incomeForm.category}
                  onChange={(e) => setIncomeForm({ ...incomeForm, category: e.target.value })}
                >
                  <option value="Audition Fee">Audition Fee</option>
                  <option value="Platform Commission">Platform Commission</option>
                  <option value="Direct Client">Direct Client</option>
                  <option value="Sponsorship">Sponsorship</option>
                  <option value="Misc">Misc</option>
                </Form.Select>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col col={6}>
                <Form.Label className="small text-secondary">Income Date</Form.Label>
                <Form.Control
                  type="date"
                  className="bg-black text-white border-secondary"
                  value={incomeForm.income_date}
                  onChange={(e) => setIncomeForm({ ...incomeForm, income_date: e.target.value })}
                />
              </Col>
              <Col col={6}>
                <Form.Label className="small text-secondary">Payment Method</Form.Label>
                <Form.Select
                  className="bg-black text-white border-secondary"
                  value={incomeForm.payment_method}
                  onChange={(e) => setIncomeForm({ ...incomeForm, payment_method: e.target.value })}
                >
                  <option value="UPI">UPI</option>
                  <option value="CARD">Card</option>
                  <option value="NET_BANKING">Net Banking</option>
                  <option value="CASH">Cash</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                </Form.Select>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="small text-secondary">Reference / Transaction ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="TXN / UPI Ref ID"
                className="bg-black text-white border-secondary"
                value={incomeForm.reference_id || ''}
                onChange={(e) => setIncomeForm({ ...incomeForm, reference_id: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-dark border-secondary">
            <Button variant="secondary" size="sm" onClick={() => setShowIncomeModal(false)}>Cancel</Button>
            <Button variant="danger" size="sm" type="submit">Save Income Entry</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL: ADD / EDIT EXPENSE */}
      <Modal show={showExpenseModal} onHide={() => setShowExpenseModal(false)} centered>
        <Modal.Header closeButton className="bg-dark text-white border-secondary">
          <Modal.Title className="fs-6 fw-bold">{editingExpense ? 'Edit Expense Entry' : 'Add Manual Expense Entry'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveExpense}>
          <Modal.Body className="bg-dark text-white">
            <Form.Group className="mb-3">
              <Form.Label className="small text-secondary">Expense Title / Description *</Form.Label>
              <Form.Control
                type="text"
                required
                placeholder="e.g. AWS Server Hosting / Google Ads"
                className="bg-black text-white border-secondary"
                value={expenseForm.title}
                onChange={(e) => setExpenseForm({ ...expenseForm, title: e.target.value })}
              />
            </Form.Group>

            <Row className="g-3 mb-3">
              <Col col={6}>
                <Form.Label className="small text-secondary">Amount (INR) *</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  required
                  placeholder="0.00"
                  className="bg-black text-white border-secondary"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                />
              </Col>
              <Col col={6}>
                <Form.Label className="small text-secondary">Category</Form.Label>
                <Form.Select
                  className="bg-black text-white border-secondary"
                  value={expenseForm.category}
                  onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                >
                  <option value="Hosting & Server">Hosting & Server</option>
                  <option value="Marketing & Ads">Marketing & Ads</option>
                  <option value="Salary / Payroll">Salary / Payroll</option>
                  <option value="Software License">Software License</option>
                  <option value="Office Maintenance">Office Maintenance</option>
                  <option value="Gateway Charges">Gateway Charges</option>
                  <option value="Misc">Misc</option>
                </Form.Select>
              </Col>
            </Row>

            <Row className="g-3 mb-3">
              <Col col={6}>
                <Form.Label className="small text-secondary">Expense Date</Form.Label>
                <Form.Control
                  type="date"
                  className="bg-black text-white border-secondary"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                />
              </Col>
              <Col col={6}>
                <Form.Label className="small text-secondary">Vendor Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. AWS / Razorpay / Facebook"
                  className="bg-black text-white border-secondary"
                  value={expenseForm.vendor_name || ''}
                  onChange={(e) => setExpenseForm({ ...expenseForm, vendor_name: e.target.value })}
                />
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="small text-secondary">Payment Method</Form.Label>
              <Form.Select
                className="bg-black text-white border-secondary"
                value={expenseForm.payment_method}
                onChange={(e) => setExpenseForm({ ...expenseForm, payment_method: e.target.value })}
              >
                <option value="UPI">UPI</option>
                <option value="CARD">Card</option>
                <option value="NET_BANKING">Net Banking</option>
                <option value="CASH">Cash</option>
                <option value="BANK_TRANSFER">Bank Transfer</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-dark border-secondary">
            <Button variant="secondary" size="sm" onClick={() => setShowExpenseModal(false)}>Cancel</Button>
            <Button variant="warning" size="sm" type="submit">Save Expense Entry</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODAL: BULK CSV / JSON UPLOAD */}
      <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-dark text-white border-secondary">
          <Modal.Title className="fs-6 fw-bold">Bulk {bulkType === 'income' ? 'Income' : 'Expense'} CSV/JSON Upload</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-white">
          <p className="text-secondary small">
            Paste your raw CSV or JSON array below. Headers should include: <code>title, category, amount, date, payment_method</code>
          </p>
          <Form.Control
            as="textarea"
            rows={10}
            className="bg-black text-white border-secondary font-monospace small"
            placeholder={
              bulkType === 'income'
                ? `title,category,amount,income_date,payment_method\n"Direct Client Fee","Audition Fee",5000,"2026-10-01","UPI"\n"Sponsorship Payment","Sponsorship",15000,"2026-10-05","BANK_TRANSFER"`
                : `title,category,amount,expense_date,vendor_name\n"AWS Cloud Hosting","Hosting & Server",3500,"2026-10-01","Amazon"\n"Meta Ad Spend","Marketing & Ads",8000,"2026-10-04","Facebook"`
            }
            value={bulkInput}
            onChange={(e) => setBulkInput(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary">
          <Button variant="secondary" size="sm" onClick={() => setShowBulkModal(false)}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleBulkSubmit}>Process Bulk Upload</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminFinancePage;
